import ExcelJS from "exceljs";
import { buscarBaseLegalPorVersao } from "../services/api-piscofins.service";
import { buscarPlanTribBaseLegal } from "../services/api-planejamento-tributario.service";

/**
 * Formata CNPJ para exibição
 */
const formatarCnpj = (cnpj) => {
  if (!cnpj) return "";
  const cnpjLimpo = cnpj.replace(/\D/g, "");
  if (cnpjLimpo.length === 14) {
    return cnpjLimpo.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  }
  return cnpj;
};

/**
 * Formata número no padrão brasileiro (com vírgula como separador decimal)
 * Ex: 1234.56 -> "1.234,56"
 */
const formatarNumeroBrasileiro = (numero) => {
  if (numero === null || numero === undefined || numero === "") return "0,00";
  const num = typeof numero === "number" ? numero : parseFloat(numero) || 0;
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

/**
 * Exporta dados do Resumo Geral para Excel
 * @param {Object} params - Parâmetros de exportação
 * @param {Array} params.oportunidades - Array de oportunidades identificadas
 * @param {Object} params.totais - Totais a serem exibidos
 * @param {Object} params.creditosAdministrativos - Créditos administrativos
 * @param {Object} params.creditosJudiciais - Créditos judiciais
 * @param {Array} params.tiposPrevia - Array de tipos de prévia com fundamentacao completa
 * @param {Object} params.infoCabecalho - Informações do cabeçalho
 * @param {number} params.versaoId - ID da versão para buscar dados das bases legais
 * @param {Object} params.valoresSalvosPorScript - Valores salvos por script
 */
export const exportarResumoGeralExcel = async ({
  oportunidades = [],
  totais = {},
  creditosAdministrativos = {},
  creditosJudiciais = {},
  tiposPrevia = [],
  infoCabecalho = {},
  versaoId,
  valoresSalvosPorScript = {},
}) => {
  try {
    if (!Array.isArray(oportunidades) || oportunidades.length === 0) {
      throw new Error("Nenhum dado disponível para exportar");
    }

    // Criar workbook
    const workbook = new ExcelJS.Workbook();
    
    // Planilha 1: Resumo Geral
    const worksheet = workbook.addWorksheet("Resumo Geral");

    // Preparar dados da tabela
    const dadosExportacao = oportunidades.map((oportunidade) => {
      return {
        oportunidade: oportunidade.oportunidade || "-",
        periodoApurado: oportunidade.periodoApurado || "-",
        fundamentoLegal: (oportunidade.fundamentoLegal || "-").replace(/\n/g, " "),
        tipo: oportunidade.tipo || "-",
        valorPis: formatarNumeroBrasileiro(oportunidade.valorPis || 0),
        valorCofins: formatarNumeroBrasileiro(oportunidade.valorCofins || 0),
        total: formatarNumeroBrasileiro(oportunidade.total || 0),
      };
    });

    // Configurar larguras das colunas
    worksheet.columns = [
      { header: "Oportunidade", key: "oportunidade", width: 30 },
      { header: "Período Apurado", key: "periodoApurado", width: 20 },
      { header: "Fundamento Legal / Tese Judicial", key: "fundamentoLegal", width: 50 },
      { header: "Tipo", key: "tipo", width: 15 },
      { header: "Valor PIS (R$)", key: "valorPis", width: 18 },
      { header: "Valor COFINS (R$)", key: "valorCofins", width: 18 },
      { header: "Total (R$)", key: "total", width: 18 },
    ];

    // Linha 1: Título
    worksheet.mergeCells(1, 1, 1, 7);
    const titleCell = worksheet.getCell(1, 1);
    titleCell.value = "RESUMO GERAL - PIS/COFINS";
    titleCell.font = { name: "Arial", size: 16, bold: true, color: { argb: "FFFFFFFF" } };
    titleCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF1E3A8A" },
    };
    titleCell.alignment = { vertical: "middle", horizontal: "center" };
    titleCell.border = {
      top: { style: "thin" },
      bottom: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
    };

    // Linha 2: Empresa
    worksheet.mergeCells(2, 1, 2, 7);
    const empresaCell = worksheet.getCell(2, 1);
    empresaCell.value = `Empresa: ${infoCabecalho.empresaNome || "Não informado"}`;
    empresaCell.font = { name: "Arial", size: 11, bold: true };
    empresaCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF8FAFC" },
    };
    empresaCell.alignment = { vertical: "middle", horizontal: "left" };
    empresaCell.border = {
      top: { style: "thin" },
      bottom: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
    };

    // Linha 3: CNPJ
    worksheet.mergeCells(3, 1, 3, 7);
    const cnpjCell = worksheet.getCell(3, 1);
    cnpjCell.value = `CNPJ: ${formatarCnpj(infoCabecalho.cnpjEmpresa) || "Não informado"}`;
    cnpjCell.font = { name: "Arial", size: 11, bold: true };
    cnpjCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF8FAFC" },
    };
    cnpjCell.alignment = { vertical: "middle", horizontal: "left" };
    cnpjCell.border = {
      top: { style: "thin" },
      bottom: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
    };

    // Linha 4: Período
    worksheet.mergeCells(4, 1, 4, 7);
    const periodoCell = worksheet.getCell(4, 1);
    periodoCell.value = `Período: ${infoCabecalho.periodoFormatado || "Não informado"}`;
    periodoCell.font = { name: "Arial", size: 11, bold: true };
    periodoCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF8FAFC" },
    };
    periodoCell.alignment = { vertical: "middle", horizontal: "left" };
    periodoCell.border = {
      top: { style: "thin" },
      bottom: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
    };

    // Linha 5: Temas
    worksheet.mergeCells(5, 1, 5, 7);
    const temasCell = worksheet.getCell(5, 1);
    temasCell.value = `Temas Selecionados: ${infoCabecalho.temasSelecionados || "Não informado"}`;
    temasCell.font = { name: "Arial", size: 11, bold: true };
    temasCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF8FAFC" },
    };
    temasCell.alignment = { vertical: "middle", horizontal: "left" };
    temasCell.border = {
      top: { style: "thin" },
      bottom: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
    };

    // Linha 6: Vazia
    worksheet.insertRow(6);

    // Linha 7: Título Totais
    worksheet.mergeCells(7, 1, 7, 7);
    const tituloTotaisCell = worksheet.getCell(7, 1);
    tituloTotaisCell.value = "TOTAIS";
    tituloTotaisCell.font = { name: "Arial", size: 12, bold: true, color: { argb: "FFFFFFFF" } };
    tituloTotaisCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF2563EB" },
    };
    tituloTotaisCell.alignment = { vertical: "middle", horizontal: "center" };
    tituloTotaisCell.border = {
      top: { style: "thin" },
      bottom: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
    };

    // Linha 8: Totais (3 colunas)
    worksheet.mergeCells(8, 1, 8, 2);
    const totalPisCell = worksheet.getCell(8, 1);
    totalPisCell.value = `Total PIS: ${formatarNumeroBrasileiro(totais.totalPis || 0)}`;
    totalPisCell.font = { name: "Arial", size: 11, bold: true };
    totalPisCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFDBEAFE" },
    };
    totalPisCell.alignment = { vertical: "middle", horizontal: "center" };
    totalPisCell.border = {
      top: { style: "thin" },
      bottom: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
    };

    worksheet.mergeCells(8, 3, 8, 4);
    const totalCofinsCell = worksheet.getCell(8, 3);
    totalCofinsCell.value = `Total COFINS: ${formatarNumeroBrasileiro(totais.totalCofins || 0)}`;
    totalCofinsCell.font = { name: "Arial", size: 11, bold: true };
    totalCofinsCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFFEE2E2" },
    };
    totalCofinsCell.alignment = { vertical: "middle", horizontal: "center" };
    totalCofinsCell.border = {
      top: { style: "thin" },
      bottom: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
    };

    worksheet.mergeCells(8, 5, 8, 7);
    const economiaCell = worksheet.getCell(8, 5);
    economiaCell.value = `Economia Total: ${formatarNumeroBrasileiro(totais.economiaTotal || 0)}`;
    economiaCell.font = { name: "Arial", size: 11, bold: true };
    economiaCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD1FAE5" },
    };
    economiaCell.alignment = { vertical: "middle", horizontal: "center" };
    economiaCell.border = {
      top: { style: "thin" },
      bottom: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
    };

    // Linha 9: Vazia
    worksheet.insertRow(9);

    // Linha 10: Título Distribuição
    worksheet.mergeCells(10, 1, 10, 7);
    const tituloDistribuicaoCell = worksheet.getCell(10, 1);
    tituloDistribuicaoCell.value = "DISTRIBUIÇÃO POR TIPO DE CRÉDITO";
    tituloDistribuicaoCell.font = { name: "Arial", size: 12, bold: true, color: { argb: "FFFFFFFF" } };
    tituloDistribuicaoCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF1E3A8A" },
    };
    tituloDistribuicaoCell.alignment = { vertical: "middle", horizontal: "center" };
    tituloDistribuicaoCell.border = {
      top: { style: "thin" },
      bottom: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
    };

    // Linha 11: Créditos Administrativos
    worksheet.mergeCells(11, 1, 11, 3);
    const tituloAdminCell = worksheet.getCell(11, 1);
    tituloAdminCell.value = "💼 Créditos Administrativos";
    tituloAdminCell.font = { name: "Arial", size: 11, bold: true };
    tituloAdminCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF8FAFC" },
    };
    tituloAdminCell.alignment = { vertical: "middle", horizontal: "center" };
    tituloAdminCell.border = {
      top: { style: "thin" },
      bottom: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
    };

    worksheet.mergeCells(11, 4, 11, 7);
    const valoresAdminCell = worksheet.getCell(11, 4);
    valoresAdminCell.value = `PIS: ${formatarNumeroBrasileiro(creditosAdministrativos.pis || 0)} | COFINS: ${formatarNumeroBrasileiro(creditosAdministrativos.cofins || 0)} | Total: ${formatarNumeroBrasileiro(creditosAdministrativos.total || 0)}`;
    valoresAdminCell.font = { name: "Arial", size: 10, bold: true };
    valoresAdminCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFDBEAFE" },
    };
    valoresAdminCell.alignment = { vertical: "middle", horizontal: "center" };
    valoresAdminCell.border = {
      top: { style: "thin" },
      bottom: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
    };

    // Linha 12: Créditos Judiciais
    worksheet.mergeCells(12, 1, 12, 3);
    const tituloJudicialCell = worksheet.getCell(12, 1);
    tituloJudicialCell.value = "⚖️ Créditos Judiciais";
    tituloJudicialCell.font = { name: "Arial", size: 11, bold: true };
    tituloJudicialCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF8FAFC" },
    };
    tituloJudicialCell.alignment = { vertical: "middle", horizontal: "center" };
    tituloJudicialCell.border = {
      top: { style: "thin" },
      bottom: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
    };

    worksheet.mergeCells(12, 4, 12, 7);
    const valoresJudicialCell = worksheet.getCell(12, 4);
    valoresJudicialCell.value = `PIS: ${formatarNumeroBrasileiro(creditosJudiciais.pis || 0)} | COFINS: ${formatarNumeroBrasileiro(creditosJudiciais.cofins || 0)} | Total: ${formatarNumeroBrasileiro(creditosJudiciais.total || 0)}`;
    valoresJudicialCell.font = { name: "Arial", size: 10, bold: true };
    valoresJudicialCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFFCE7F3" },
    };
    valoresJudicialCell.alignment = { vertical: "middle", horizontal: "center" };
    valoresJudicialCell.border = {
      top: { style: "thin" },
      bottom: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
    };

    // Linha 13: Vazia
    worksheet.insertRow(13);

    // Linha 14: Cabeçalho da tabela
    const headerRow = worksheet.addRow(worksheet.columns.map((col) => col.header));
    headerRow.font = { name: "Arial", size: 11, bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF1F2A44" },
    };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };
    headerRow.eachCell((cell) => {
      cell.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };
    });
    
    // Alinhar valores monetários no cabeçalho à direita
    headerRow.getCell("valorPis").alignment = { vertical: "middle", horizontal: "right" };
    headerRow.getCell("valorCofins").alignment = { vertical: "middle", horizontal: "right" };
    headerRow.getCell("total").alignment = { vertical: "middle", horizontal: "right" };

    // Adicionar dados
    dadosExportacao.forEach((linha, index) => {
      const row = worksheet.addRow(linha);
      row.font = { name: "Arial", size: 10 };
      
      // Alternar cores de fundo para linhas
      if (index % 2 === 0) {
        row.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFFFFF" },
        };
      } else {
        row.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFBFBFF" },
        };
      }
      
      // Adicionar bordas
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin", color: { argb: "FFE1E6EE" } },
          bottom: { style: "thin", color: { argb: "FFE1E6EE" } },
          left: { style: "thin", color: { argb: "FFE1E6EE" } },
          right: { style: "thin", color: { argb: "FFE1E6EE" } },
        };
      });
      
      // Alinhar valores monetários à direita
      row.getCell("valorPis").alignment = { vertical: "middle", horizontal: "right" };
      row.getCell("valorCofins").alignment = { vertical: "middle", horizontal: "right" };
      row.getCell("total").alignment = { vertical: "middle", horizontal: "right" };
    });

    // Linha de totais
    const totalRow = worksheet.addRow({
      oportunidade: "TOTAL GERAL",
      periodoApurado: "",
      fundamentoLegal: "",
      tipo: "",
      valorPis: formatarNumeroBrasileiro(totais.totalPis || 0),
      valorCofins: formatarNumeroBrasileiro(totais.totalCofins || 0),
      total: formatarNumeroBrasileiro(totais.economiaTotal || 0),
    });

    totalRow.font = { name: "Arial", size: 11, bold: true, color: { argb: "FFFFFFFF" } };
    totalRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF1F2A44" },
    };
    
    // Adicionar bordas na linha de totais
    totalRow.eachCell((cell) => {
      cell.border = {
        top: { style: "medium" },
        bottom: { style: "medium" },
        left: { style: "thin" },
        right: { style: "thin" },
      };
    });
    
    // Alinhar valores monetários na linha de totais à direita
    totalRow.getCell("valorPis").alignment = { vertical: "middle", horizontal: "right" };
    totalRow.getCell("valorCofins").alignment = { vertical: "middle", horizontal: "right" };
    totalRow.getCell("total").alignment = { vertical: "middle", horizontal: "right" };

    // Buscar bases legais (usado em todas as abas de base legal)
    let basesLegais = [];
    if (versaoId) {
      try {
        const resultadoBasesLegais = await buscarPlanTribBaseLegal();
        basesLegais = Array.isArray(resultadoBasesLegais?.itens)
          ? resultadoBasesLegais.itens
          : Array.isArray(resultadoBasesLegais)
          ? resultadoBasesLegais
          : [];
      } catch (error) {
        console.warn("⚠️ Erro ao buscar bases legais:", error);
      }
    }

    // Criar planilhas adicionais para Base Legal de cada tipo com valores salvos
    if (versaoId) {
      const scriptsComValores = Object.keys(valoresSalvosPorScript).filter(
        (script) => valoresSalvosPorScript[script] && (valoresSalvosPorScript[script].pis > 0 || valoresSalvosPorScript[script].cofins > 0)
      );

      for (const script of scriptsComValores) {
        try {
          // Buscar dados agrupados por base legal
          const resultado = await buscarBaseLegalPorVersao(versaoId);
          const linhasBaseLegal = Array.isArray(resultado?.linhas) ? resultado.linhas : [];
          const totaisBaseLegal = resultado?.totais && typeof resultado.totais === "object" ? resultado.totais : {};

          if (resultado?.sucesso && linhasBaseLegal.length > 0) {
            // Buscar nome do script nos tipos de prévia
            const tipoPrevia = tiposPrevia.find((t) => t.script === script);
            const nomeScript = tipoPrevia?.descricao || script;

            // Criar planilha para este tipo
            const baseLegalSheet = workbook.addWorksheet(nomeScript.length > 31 ? nomeScript.substring(0, 31) : nomeScript);

            // Preparar dados da tabela
            const dadosBaseLegal = linhasBaseLegal.map((linha) => {
              const baseLegal = basesLegais.find((b) => b.id === linha.baseLegalId);
              const contaContabil = baseLegal?.descricao || `Base Legal #${linha.baseLegalId}`;
              const baseLegalCredito = baseLegal?.baseLegalCredito || "";

              return {
                contaContabil,
                valorTotal: formatarNumeroBrasileiro(linha.valorTotalEcd),
                pis: formatarNumeroBrasileiro(linha.pis),
                cofins: formatarNumeroBrasileiro(linha.cofins),
                baseLegalCredito,
              };
            });

            // Configurar larguras das colunas
            baseLegalSheet.columns = [
              { header: "Conta Contábil", key: "contaContabil", width: 40 },
              { header: "Valor Total", key: "valorTotal", width: 18 },
              { header: "PIS", key: "pis", width: 18 },
              { header: "COFINS", key: "cofins", width: 18 },
              { header: "Base Legal de Crédito", key: "baseLegalCredito", width: 60 },
            ];

            // Linha 1: Título
            baseLegalSheet.mergeCells(1, 1, 1, 5);
            const titleBaseLegal = baseLegalSheet.getCell(1, 1);
            titleBaseLegal.value = `${nomeScript.toUpperCase()} - BASE LEGAL`;
            titleBaseLegal.font = { name: "Arial", size: 16, bold: true, color: { argb: "FFFFFFFF" } };
            titleBaseLegal.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FF1E3A8A" },
            };
            titleBaseLegal.alignment = { vertical: "middle", horizontal: "center" };
            titleBaseLegal.border = {
              top: { style: "thin" },
              bottom: { style: "thin" },
              left: { style: "thin" },
              right: { style: "thin" },
            };

            // Linha 2: Empresa
            baseLegalSheet.mergeCells(2, 1, 2, 5);
            const empresaBaseLegal = baseLegalSheet.getCell(2, 1);
            empresaBaseLegal.value = `Empresa: ${infoCabecalho.empresaNome || "Não informado"}`;
            empresaBaseLegal.font = { name: "Arial", size: 11, bold: true };
            empresaBaseLegal.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF8FAFC" },
            };
            empresaBaseLegal.alignment = { vertical: "middle", horizontal: "left" };
            empresaBaseLegal.border = {
              top: { style: "thin" },
              bottom: { style: "thin" },
              left: { style: "thin" },
              right: { style: "thin" },
            };

            // Linha 3: CNPJ
            baseLegalSheet.mergeCells(3, 1, 3, 5);
            const cnpjBaseLegal = baseLegalSheet.getCell(3, 1);
            cnpjBaseLegal.value = `CNPJ: ${formatarCnpj(infoCabecalho.cnpjEmpresa) || "Não informado"}`;
            cnpjBaseLegal.font = { name: "Arial", size: 11, bold: true };
            cnpjBaseLegal.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF8FAFC" },
            };
            cnpjBaseLegal.alignment = { vertical: "middle", horizontal: "left" };
            cnpjBaseLegal.border = {
              top: { style: "thin" },
              bottom: { style: "thin" },
              left: { style: "thin" },
              right: { style: "thin" },
            };

            // Linha 4: Período
            baseLegalSheet.mergeCells(4, 1, 4, 5);
            const periodoBaseLegal = baseLegalSheet.getCell(4, 1);
            periodoBaseLegal.value = `Período: ${infoCabecalho.periodoFormatado || "Não informado"}`;
            periodoBaseLegal.font = { name: "Arial", size: 11, bold: true };
            periodoBaseLegal.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF8FAFC" },
            };
            periodoBaseLegal.alignment = { vertical: "middle", horizontal: "left" };
            periodoBaseLegal.border = {
              top: { style: "thin" },
              bottom: { style: "thin" },
              left: { style: "thin" },
              right: { style: "thin" },
            };

            // Linha 5: Temas
            baseLegalSheet.mergeCells(5, 1, 5, 5);
            const temasBaseLegal = baseLegalSheet.getCell(5, 1);
            temasBaseLegal.value = `Temas Selecionados: ${infoCabecalho.temasSelecionados || "Não informado"}`;
            temasBaseLegal.font = { name: "Arial", size: 11, bold: true };
            temasBaseLegal.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF8FAFC" },
            };
            temasBaseLegal.alignment = { vertical: "middle", horizontal: "left" };
            temasBaseLegal.border = {
              top: { style: "thin" },
              bottom: { style: "thin" },
              left: { style: "thin" },
              right: { style: "thin" },
            };

            // Linha 6: Vazia
            baseLegalSheet.insertRow(6);

            // Linha 7: Título Totais
            baseLegalSheet.mergeCells(7, 1, 7, 5);
            const tituloTotaisBaseLegal = baseLegalSheet.getCell(7, 1);
            tituloTotaisBaseLegal.value = "TOTAIS";
            tituloTotaisBaseLegal.font = { name: "Arial", size: 12, bold: true, color: { argb: "FFFFFFFF" } };
            tituloTotaisBaseLegal.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FF2563EB" },
            };
            tituloTotaisBaseLegal.alignment = { vertical: "middle", horizontal: "center" };
            tituloTotaisBaseLegal.border = {
              top: { style: "thin" },
              bottom: { style: "thin" },
              left: { style: "thin" },
              right: { style: "thin" },
            };

            // Linha 8: Totais (3 colunas)
            baseLegalSheet.mergeCells(8, 1, 8, 1);
            const totalEcdBaseLegal = baseLegalSheet.getCell(8, 1);
            totalEcdBaseLegal.value = `Total ECD: ${formatarNumeroBrasileiro(totaisBaseLegal?.valorTotalEcd || 0)}`;
            totalEcdBaseLegal.font = { name: "Arial", size: 11, bold: true };
            totalEcdBaseLegal.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFDBEAFE" },
            };
            totalEcdBaseLegal.alignment = { vertical: "middle", horizontal: "center" };
            totalEcdBaseLegal.border = {
              top: { style: "thin" },
              bottom: { style: "thin" },
              left: { style: "thin" },
              right: { style: "thin" },
            };

            baseLegalSheet.mergeCells(8, 2, 8, 3);
            const totalPisBaseLegal = baseLegalSheet.getCell(8, 2);
            totalPisBaseLegal.value = `Total PIS: ${formatarNumeroBrasileiro(totaisBaseLegal?.pis || 0)}`;
            totalPisBaseLegal.font = { name: "Arial", size: 11, bold: true };
            totalPisBaseLegal.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFDBEAFE" },
            };
            totalPisBaseLegal.alignment = { vertical: "middle", horizontal: "center" };
            totalPisBaseLegal.border = {
              top: { style: "thin" },
              bottom: { style: "thin" },
              left: { style: "thin" },
              right: { style: "thin" },
            };

            baseLegalSheet.mergeCells(8, 4, 8, 5);
            const totalCofinsBaseLegal = baseLegalSheet.getCell(8, 4);
            totalCofinsBaseLegal.value = `Total COFINS: ${formatarNumeroBrasileiro(totaisBaseLegal?.cofins || 0)}`;
            totalCofinsBaseLegal.font = { name: "Arial", size: 11, bold: true };
            totalCofinsBaseLegal.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFFEE2E2" },
            };
            totalCofinsBaseLegal.alignment = { vertical: "middle", horizontal: "center" };
            totalCofinsBaseLegal.border = {
              top: { style: "thin" },
              bottom: { style: "thin" },
              left: { style: "thin" },
              right: { style: "thin" },
            };

            // Linha 9: Vazia
            baseLegalSheet.insertRow(9);

            // Linha 10: Cabeçalho da tabela
            const headerBaseLegal = baseLegalSheet.addRow(baseLegalSheet.columns.map((col) => col.header));
            headerBaseLegal.font = { name: "Arial", size: 11, bold: true, color: { argb: "FFFFFFFF" } };
            headerBaseLegal.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FF1F2A44" },
            };
            headerBaseLegal.alignment = { vertical: "middle", horizontal: "center" };
            headerBaseLegal.eachCell((cell) => {
              cell.border = {
                top: { style: "thin" },
                bottom: { style: "thin" },
                left: { style: "thin" },
                right: { style: "thin" },
              };
            });
            
            // Alinhar valores monetários no cabeçalho à direita
            headerBaseLegal.getCell("valorTotal").alignment = { vertical: "middle", horizontal: "right" };
            headerBaseLegal.getCell("pis").alignment = { vertical: "middle", horizontal: "right" };
            headerBaseLegal.getCell("cofins").alignment = { vertical: "middle", horizontal: "right" };

            // Adicionar dados
            dadosBaseLegal.forEach((linha, index) => {
              const row = baseLegalSheet.addRow(linha);
              row.font = { name: "Arial", size: 10 };
              
              // Alternar cores de fundo para linhas
              if (index % 2 === 0) {
                row.fill = {
                  type: "pattern",
                  pattern: "solid",
                  fgColor: { argb: "FFFFFFFF" },
                };
              } else {
                row.fill = {
                  type: "pattern",
                  pattern: "solid",
                  fgColor: { argb: "FFFBFBFF" },
                };
              }
              
              // Adicionar bordas
              row.eachCell((cell) => {
                cell.border = {
                  top: { style: "thin", color: { argb: "FFE1E6EE" } },
                  bottom: { style: "thin", color: { argb: "FFE1E6EE" } },
                  left: { style: "thin", color: { argb: "FFE1E6EE" } },
                  right: { style: "thin", color: { argb: "FFE1E6EE" } },
                };
              });
              
              // Alinhar valores monetários à direita
              row.getCell("valorTotal").alignment = { vertical: "middle", horizontal: "right" };
              row.getCell("pis").alignment = { vertical: "middle", horizontal: "right" };
              row.getCell("cofins").alignment = { vertical: "middle", horizontal: "right" };
            });

            // Linha de totais
            const totalBaseLegal = baseLegalSheet.addRow({
              contaContabil: "TOTAL",
              valorTotal: formatarNumeroBrasileiro(totaisBaseLegal?.valorTotalEcd || 0),
              pis: formatarNumeroBrasileiro(totaisBaseLegal?.pis || 0),
              cofins: formatarNumeroBrasileiro(totaisBaseLegal?.cofins || 0),
              baseLegalCredito: "",
            });

            totalBaseLegal.font = { name: "Arial", size: 11, bold: true, color: { argb: "FFFFFFFF" } };
            totalBaseLegal.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FF1F2A44" },
            };
            
            // Adicionar bordas na linha de totais
            totalBaseLegal.eachCell((cell) => {
              cell.border = {
                top: { style: "medium" },
                bottom: { style: "medium" },
                left: { style: "thin" },
                right: { style: "thin" },
              };
            });
            
            // Alinhar valores monetários na linha de totais à direita
            totalBaseLegal.getCell("valorTotal").alignment = { vertical: "middle", horizontal: "right" };
            totalBaseLegal.getCell("pis").alignment = { vertical: "middle", horizontal: "right" };
            totalBaseLegal.getCell("cofins").alignment = { vertical: "middle", horizontal: "right" };
          }
        } catch (error) {
          console.warn(`⚠️ Erro ao buscar dados do script ${script}:`, error);
          // Continuar com outros scripts mesmo se um falhar
        }
      }
    }

    // Gerar nome do arquivo
    const dataAtual = new Date().toISOString().split("T")[0].replace(/-/g, "");
    const cnpjLimpo = infoCabecalho.cnpjEmpresa
      ? infoCabecalho.cnpjEmpresa.replace(/\D/g, "")
      : "";
    const nomeArquivo =
      cnpjLimpo && cnpjLimpo.length === 14
        ? `ResumoGeral_${cnpjLimpo}_${dataAtual}.xlsx`
        : `ResumoGeral_${dataAtual}.xlsx`;

    // Gerar arquivo Excel
    const buffer = await workbook.xlsx.writeBuffer();

    // Criar blob e fazer download
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = nomeArquivo;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return {
      sucesso: true,
      nomeArquivo,
    };
  } catch (error) {
    console.error("Erro ao exportar Resumo Geral para Excel:", error);
    throw new Error(
      error.message || "Erro ao gerar arquivo Excel. Por favor, tente novamente."
    );
  }
};

